import * as scmpAccount from "@product/scmp-account-sdk";
import { useRouter } from "next/router";
import type { FunctionComponent } from "react";
import { useCallback, useEffect, useState } from "react";

import { useLoginDialogStateHelper } from "~/components/login-dialog/hooks";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { useCurrentArticleIds } from "~/lib/current-item";
import { useArticleCommentLink } from "~/lib/hooks";
import { parseQueryString } from "~/lib/utils";

import { CloseCommentEvent, LoginSuccessQueryParameters } from "./consts";
import { CommentContainer, StyledDrawer, StyledIFrame } from "./styles";

export const ArticleCommentsDrawer: FunctionComponent = () => {
  const [showComments, setShowComments] = useState(false);
  const { query } = useRouter();
  const [iframeUrl, setIframeUrl] = useState("");
  const { isCommentLink, toggleByUrl: toggleCommentByUrl } = useArticleCommentLink();
  const { openLoginDialog } = useLoginDialogStateHelper();

  useEffect(() => {
    setShowComments(isCommentLink());
  }, [isCommentLink]);

  const { currentArticleId } = useCurrentArticleIds();
  useEffect(() => {
    if (!showComments || !currentArticleId) return;

    const commentUrl = new URL(location.origin + `/scmp_comments/popup/${currentArticleId}`);
    const commentId = parseQueryString(query.commentId);

    if (commentId) {
      commentUrl.searchParams.set("commentID", commentId);
    }

    setIframeUrl(commentUrl.toString());
  }, [showComments, query, currentArticleId]);

  const handleCloseDrawer = useCallback(() => {
    toggleCommentByUrl();
  }, [toggleCommentByUrl]);

  useEffect(() => {
    const handleCloseCommentEvent = (event: Event) => {
      if (event.data.event === CloseCommentEvent) {
        handleCloseDrawer();
      }
    };

    window.addEventListener("message", handleCloseCommentEvent);

    return () => {
      window.removeEventListener("message", handleCloseCommentEvent);
    };
  }, [handleCloseDrawer]);

  useEffect(() => {
    const handler = (event: MessageEvent<{ event: string; url: string }>) => {
      if (event.data.event !== "openLoginLayer") return;
      const accountUrl = new URL(event.data.url);
      accountUrl.searchParams.set(LoginSuccessQueryParameters, "1");
      openLoginDialog({
        description:
          "Login or register to join in the discussion, plus read what others have to say.",
        destination: accountUrl.toString(),
        ga4CustomParameter: {
          trigger_point: "comment",
        },
        title: "Contribute to the conversation today",
      });
      subscribeRegistrationSuccessEvent();
    };

    let unsubscribeEmailRegistrationSuccess: false | (() => void);
    const subscribeRegistrationSuccessEvent = () => {
      unsubscribeEmailRegistrationSuccess = scmpAccount.on(
        scmpAccount.EVENT.EMAIL_REGISTRATION_SUCCESS,
        () => {
          sendGATracking({
            action: "Comment button/Login/success",
            category: "Comment",
          });
        },
      );
    };

    window.addEventListener("message", handler);

    return () => {
      window.removeEventListener("message", handler);
      if (typeof unsubscribeEmailRegistrationSuccess === "function") {
        unsubscribeEmailRegistrationSuccess();
      }
    };
  }, [openLoginDialog]);

  return (
    <StyledDrawer
      anchor="right"
      open={showComments && iframeUrl !== ""}
      onClose={handleCloseDrawer}
    >
      <CommentContainer>
        <StyledIFrame src={iframeUrl} />
      </CommentContainer>
    </StyledDrawer>
  );
};

ArticleCommentsDrawer.displayName = "ArticleCommentsDrawer";

type Event = {
  data: {
    event: string;
  };
};
